import { toast } from 'sonner';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarCheck, Check, Loader2, Pencil, X,
} from 'lucide-react';
import { Hint } from '@/components/Hint';
import { Input } from '@/components/ui/input';
import { statusPillClass } from '@/lib/status';
import { patchOrderDates, type OrderSummary } from '@/api/orders';
import { getDraftOrders, type DraftSummary } from '@/api/orderPrep';
import { Can } from '@/auth/guards';
import { useAuth } from '@/auth/useAuth';
import { dueTone, fmtDate, fmtDateLong, toOrderCompare } from './lib';
import { DraftChip, OrderPrepDetail } from './OrderPrepSection';
import { todayOrderAnchor, todayOrderJourneys } from '@/features/today/todayLib';
import { useTodayStore } from '@/stores/todayStore';
import { JourneyName, RowAction, WorkEmpty, WorkNotice, WorkRow } from './workLib';
import { todayDateString } from './workUtils';

/** Inline date pencil — the deadline ladder is PATCH-able, so fixing a misread
 *  window is one click, not a conversation with the agent. */
function DatePencil({ label, value, onSave }: {
  label: string;
  value: string | null;
  onSave: (date: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [busy, setBusy] = useState(false);
  if (!editing) {
    return (
      <Hint content={`Edit ${label}`}>
        <button
          type="button"
          aria-label={`Edit ${label}`} className="inline-flex h-8 w-8 items-center justify-center pointer-coarse:size-11 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          onClick={(e) => { e.stopPropagation(); setDraft(value ?? ''); setEditing(true); }}
        >
          <Pencil className="h-3 w-3" />
        </button>
      </Hint>
    );
  }
  return (
    <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <Input
        type="date"
        aria-label={label}
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="h-6 w-36 px-1.5 text-xs pointer-coarse:h-9 pointer-coarse:w-40"
      />
      <button
        type="button"
        aria-label={`Save ${label}`} className="inline-flex h-8 w-8 items-center justify-center pointer-coarse:size-11 text-foreground hover:opacity-80 disabled:opacity-40"
        disabled={busy || !draft}
        onClick={async () => {
          setBusy(true);
          try { await onSave(draft); setEditing(false); toast.success('Date saved'); } catch (error) { toast.error(error instanceof Error ? error.message : 'Could not save date'); } finally { setBusy(false); }
        }}
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
      </button>
      <button type="button" aria-label="Cancel date edit" disabled={busy} className="inline-flex h-8 w-8 items-center justify-center pointer-coarse:size-11 text-muted-foreground hover:text-foreground" onClick={() => setEditing(false)}>
        <X className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

/**
 * The ordering queue, for browsing: every announced journey, deadline first.
 * Placing it or Won't order happens on its Today order card (On Today) when
 * it has one, else on the journey page (the row's title).
 */
export function ToOrderView({ orders, onChanged, onOpenJourney }: {
  orders: OrderSummary[];
  onChanged: () => void;
  onOpenJourney: (orderId: string) => void;
}) {
  const navigate = useNavigate();
  const onToday = todayOrderJourneys(useTodayStore((s) => s.today));

  // The daily order-prep run's drafts, joined onto the queue by journey id.
  // null = not loaded yet, so rows don't flash "no draft" before the fetch.
  const [drafts, setDrafts] = useState<Map<string, DraftSummary> | null>(null);
  const [draftsError, setDraftsError] = useState<string | null>(null);
  const [prepOpen, setPrepOpen] = useState<Set<string>>(new Set());

  // Drafts are their own read (orders:read:drafts): without it the queue
  // shows the announced rows with no sizing, and asks the server for none.
  const canDrafts = useAuth().can('orders:read:drafts');
  const loadDrafts = useCallback(async () => {
    if (!canDrafts) return;
    try {
      const list = await getDraftOrders();
      setDrafts(new Map(list.map((d) => [d.orderId, d])));
      setDraftsError(null);
    } catch (e) {
      setDraftsError(e instanceof Error ? e.message : 'Failed to load prep drafts');
    }
  }, [canDrafts]);

  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const togglePrep = useCallback((orderId: string) => {
    setPrepOpen((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  }, []);

  const rows = useMemo(() => [...orders].sort(toOrderCompare), [orders]);

  if (rows.length === 0) {
    return <WorkEmpty>Nothing waiting to be ordered.</WorkEmpty>;
  }

  return (
    <div>
      {draftsError && (
        <WorkNotice tone="danger">Prep drafts failed to load: {draftsError}</WorkNotice>
      )}
      {rows.map((order) => {
        const draft = drafts?.get(order.id);
        const prepExpanded = prepOpen.has(order.id);
        const detail = prepExpanded && draft && (
          <OrderPrepDetail
            orderId={order.id}
            currency={order.currency}
            onDraftsChanged={loadDrafts}
          />
        );
        return (
          <WorkRow
            key={order.id}
            scrollKey={`order:${order.id}`}
            title={<JourneyName vendor={order.vendor} label={order.label} onOpen={() => onOpenJourney(order.id)} />}
            detail={detail || undefined}
            actions={onToday.has(order.id) && (
              <RowAction icon={<CalendarCheck className="h-3 w-3" />} onClick={() => navigate(`/today#${todayOrderAnchor(order.id)}`)}>
                On Today
              </RowAction>
            )}
          >
            {/* The one date that empties this queue, tonal by urgency. This
                row is the deadline's ONLY home (runs no longer raise actions
                for it), so its absence must read as a gap, not as calm —
                no-deadline rows sort last and their drafts never finalize.
                Setting the missing deadline is the fix, so the pencil shows
                only then; an existing date is edited on the journey. */}
            {order.orderDueDate ? (
              <Hint content={order.orderDueDate < todayDateString() ? `Lapsed ${fmtDateLong(order.orderDueDate)}; still needs a decision.` : 'Deadline to place this order.'}>
                <span className={statusPillClass(dueTone(order.orderDueDate) ?? 'neutral')}>
                  order by {fmtDate(order.orderDueDate)}
                </span>
              </Hint>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs">
                <Hint content="No deadline recorded; set one.">
                  <span className={statusPillClass('warning')}>no deadline</span>
                </Hint>
                {/* PATCH /orders/:id without status — orders:write:journeys, which this queue's own
                    page code (orders:page:to_order) does not imply. */}
                <Can code="orders:write:journeys">
                  <DatePencil
                    label="the order-by deadline"
                    value={order.orderDueDate}
                    onSave={async (d) => { await patchOrderDates(order.id, { order_due_date: d }); onChanged(); }}
                  />
                </Can>
              </span>
            )}

            {draft && (
              <DraftChip
                draft={draft}
                expanded={prepExpanded}
                currency={order.currency}
                onToggle={() => togglePrep(order.id)}
              />
            )}
          </WorkRow>
        );
      })}
    </div>
  );
}
